import { useTranslation } from "react-i18next";
import { Accordion } from "@/components/ui/accordion";
import { HelpTopic } from "./HelpTopic";
import type { HelpSection as HelpSectionDef } from "./types";

interface HelpSectionProps {
	section: HelpSectionDef;
	expandedTopics: string[];
	onExpandedChange: (values: string[]) => void;
	visibleTopicIds?: Set<string>;
}

export const HelpSection = ({ section, expandedTopics, onExpandedChange, visibleTopicIds }: HelpSectionProps) => {
	const { t } = useTranslation();
	const Icon = section.icon;

	const topics = visibleTopicIds
		? section.topics.filter((topic) => visibleTopicIds.has(`${section.id}.${topic.id}`))
		: section.topics;

	if (topics.length === 0)
		return null;

	return (
		<section id={`help-${section.id}`} className="scroll-mt-4">
			<div className="flex items-start gap-4 mb-5">
				<div className="flex-shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
					<Icon className="h-5 w-5" />
				</div>
				<div className="min-w-0">
					<h2 className="text-2xl font-semibold tracking-tight">{t(section.titleKey)}</h2>
					<p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
						{t(section.descriptionKey)}
					</p>
				</div>
			</div>
			<Accordion
				type="multiple"
				value={expandedTopics}
				onValueChange={onExpandedChange}
				className="border-t border-border"
			>
				{topics.map((topic) => (
					<HelpTopic key={topic.id} sectionId={section.id} topic={topic} />
				))}
			</Accordion>
		</section>
	);
};
